import React, { useRef, useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Minus } from "lucide-react";

interface ScrollablePickerProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
  label?: string;
}

export const ScrollablePicker = React.forwardRef<
  HTMLButtonElement,
  ScrollablePickerProps
>(({
  value,
  onChange,
  min = 0,
  max = 15,
  step = 1,
  placeholder = "Selecteer aantal",
  disabled = false,
  className,
  icon,
  label = "items",
  ...props
}, ref) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [open, setOpen] = useState(false);

  // Generate array of numbers from min to max
  const numbers = Array.from(
    { length: Math.floor((max - min) / step) + 1 },
    (_, i) => min + i * step
  );

  const ITEM_HEIGHT = 56;

  const scrollToValue = useCallback((targetValue: number, smooth = true) => {
    if (!scrollContainerRef.current) return;

    isProgrammaticScrollRef.current = true;

    const index = numbers.indexOf(targetValue);
    if (index === -1) {
      isProgrammaticScrollRef.current = false;
      return;
    }

    const targetScrollTop = index * ITEM_HEIGHT;
    scrollContainerRef.current.scrollTo({
      top: targetScrollTop,
      behavior: smooth ? 'smooth' : 'instant'
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, smooth ? 300 : 50);
  }, [numbers]);

  // Scroll to current value when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        scrollToValue(value, false);
      }, 50);
    }
  }, [open, value, scrollToValue]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    
    if (isProgrammaticScrollRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (isProgrammaticScrollRef.current) return;
      
      const scrollTop = scrollContainerRef.current?.scrollTop || 0;
      const index = Math.round(scrollTop / ITEM_HEIGHT);
      const newValue = numbers[index];

      if (newValue !== undefined && newValue !== value) {
        onChange(newValue);
      }
    }, 150);
  }, [numbers, onChange, value]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    const direction = e.deltaY > 0 ? 1 : -1;
    const currentIndex = numbers.indexOf(value);
    const newIndex = Math.max(0, Math.min(numbers.length - 1, currentIndex + direction));
    
    if (newIndex !== currentIndex) {
      const newValue = numbers[newIndex];
      onChange(newValue);
      scrollToValue(newValue, true);
    }
  }, [numbers, onChange, scrollToValue, value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = numbers.indexOf(value);
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(numbers.length - 1, currentIndex + 1);
        break;
      case 'PageUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 5);
        break;
      case 'PageDown':
        e.preventDefault();
        newIndex = Math.min(numbers.length - 1, currentIndex + 5);
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = numbers.length - 1;
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        return;
      default:
        // Handle direct number input
        if (e.key >= '0' && e.key <= '9') {
          const inputNumber = parseInt(e.key);
          if (numbers.includes(inputNumber)) {
            e.preventDefault();
            onChange(inputNumber);
            scrollToValue(inputNumber);
          }
        }
        return;
    }

    const newValue = numbers[newIndex];
    if (newValue !== value) {
      onChange(newValue);
      scrollToValue(newValue);
    }
  };

  const handleIncrement = () => {
    const currentIndex = numbers.indexOf(value);
    const newIndex = Math.min(numbers.length - 1, currentIndex + 1);
    const newValue = numbers[newIndex];
    if (newValue !== value) {
      onChange(newValue);
      scrollToValue(newValue);
    }
  };

  const handleDecrement = () => {
    const currentIndex = numbers.indexOf(value);
    const newIndex = Math.max(0, currentIndex - 1);
    const newValue = numbers[newIndex];
    if (newValue !== value) {
      onChange(newValue);
      scrollToValue(newValue);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && value !== 0 && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <div className="flex items-center w-full">
            {icon && <div className="mr-3">{icon}</div>}
            <span className="flex-1">
              {value || value === 0 ? `${value} ${label}` : placeholder}
            </span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-0" align="center">
        <div className="relative h-52 overflow-hidden">
          {/* Gradient fade-outs */}
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-popover to-transparent pointer-events-none z-20" />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-popover to-transparent pointer-events-none z-20" />
          
          {/* +/- Controls */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-background/80 hover:bg-background"
              onClick={handleIncrement}
              disabled={value >= max}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-background/80 hover:bg-background"
              onClick={handleDecrement}
              disabled={value <= min}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Enhanced center highlight */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-14 bg-accent/30 border-y-2 border-accent/50 rounded-lg mx-2 pointer-events-none z-10 shadow-lg" />
          
          {/* Scroll container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="listbox"
            aria-label={`Select ${label}`}
            aria-activedescendant={`option-${value}`}
            className="h-full overflow-y-auto overscroll-contain scrollbar-none focus:outline-none [-webkit-scrollbar:none] [scrollbar-width:none] [scrollbar-gutter:stable] [-webkit-appearance:none]"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              WebkitAppearance: 'none',
              scrollbarWidth: 'none',
              scrollbarGutter: 'stable',
              msOverflowStyle: 'none',
              overscrollBehavior: 'contain',
              scrollSnapType: 'y mandatory'
            }}
          >
            <div className="py-[4.75rem]">
              {numbers.map((number) => (
                <div
                  key={number}
                  id={`option-${number}`}
                  role="option"
                  aria-selected={number === value}
                  style={{
                    scrollSnapAlign: 'center',
                    scrollSnapStop: 'always',
                  }}
                  className={cn(
                    "h-14 flex items-center justify-center text-xl font-semibold cursor-pointer transition-all duration-150 touch-manipulation select-none",
                    number === value
                      ? "text-foreground font-bold" 
                      : number === 0 
                        ? "text-muted-foreground/70 hover:text-muted-foreground hover:scale-105"
                        : "text-muted-foreground hover:text-foreground hover:scale-105"
                  )}
                  onClick={() => {
                    onChange(number);
                    scrollToValue(number, false);
                  }}
                >
                  {number}
                </div>
              ))}
            </div>
          </div>
          
          {/* Value indicator dots */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none z-20">
            {numbers.slice(0, Math.min(5, numbers.length)).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-1.5 h-1.5 rounded-full transition-all duration-200",
                  index === Math.floor(numbers.indexOf(value) / Math.ceil(numbers.length / 5))
                    ? "bg-accent scale-125"
                    : "bg-muted-foreground/30"
                )}
              />
            ))}
          </div>
        </div>
        
        {/* Live region for accessibility */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {value} {label} selected
        </div>
      </PopoverContent>
    </Popover>
  );
});

ScrollablePicker.displayName = "ScrollablePicker";